// Phase 2.10 (hh-1hz) — AddItemScreen.
//
// Manual fallback for creating one item. Voice is the primary path; this
// form is used when voice is not viable (noise, night). Ported from
// designproject/files/screens-forms.jsx (`AddItemScreen`).
//
// Backend: POST /api/v1/items (batch shape — a single item here), scoped to
// a room picked from GET /api/v1/properties/tree. The design's "контейнер"
// and "заметка" fields have no backend support in the v1 items contract, so
// they are dropped — the form keeps name, quantity and location.
//
// The "без места" toggle is supported (aidepo-57.7): when ON we send
// orphan=true, which creates the item with no room/property (an orphan,
// case-06) and the back-end ignores any place hints.

import type { JSX } from 'preact';
import { useMemo, useState } from 'preact/hooks';
import { T, resolveIconName } from '../../tokens';
import { ScreenHeader } from '../../shell';
import type { RouterApi } from '../../router';
import { items, humanizeError } from '../../api';
import { usePropertyTree } from './usePropertyTree';
import { PlacePicker, type PlaceSelection } from './PlacePicker';
import { IconPicker } from './IconPicker';
import { FormLabel, FormInput, FormFooter, FormError } from './formPrimitives';

export interface AddItemScreenProps {
  readonly router: RouterApi;
  readonly prefill?: { readonly name?: string };
}

function describeError(err: unknown): string {
  return humanizeError(err, 'save');
}

function parseQuantity(raw: string): number {
  const n = Number.parseInt(raw, 10);
  return Number.isFinite(n) && n > 0 ? n : 1;
}

export function AddItemScreen({ router, prefill }: AddItemScreenProps): JSX.Element {
  const tree = usePropertyTree();
  const [name, setName] = useState<string>(prefill?.name ?? '');
  const [quantity, setQuantity] = useState<string>('1');
  const [place, setPlace] = useState<PlaceSelection | null>(null);
  // true = вещь без места (orphan, case-06); место не требуется и не отправляется.
  const [noPlace, setNoPlace] = useState<boolean>(false);
  // null = иконку подбираем автоматически по названию; строка = ручной выбор.
  const [icon, setIcon] = useState<string | null>(null);
  const [pending, setPending] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Авто-подобранная иконка для превью пикера в режиме «авто».
  const autoIcon = useMemo(() => resolveIconName('item', name), [name]);

  const canSave = useMemo<boolean>(
    () => name.trim().length > 0 && (noPlace || place !== null) && !pending,
    [name, place, noPlace, pending],
  );

  const back = (): void => router.back();

  const save = (): void => {
    if (name.trim().length === 0) return;
    if (!noPlace && place === null) return;
    setPending(true);
    setError(null);
    const item = {
      name: name.trim(),
      quantity: parseQuantity(quantity),
      // Шлём icon только при ручном выборе; null = авто-подбор.
      ...(icon !== null ? { icon } : {}),
    };
    // Без места: orphan=true, бэкенд игнорирует подсказки по месту (case-06).
    // С местом: передаём выбранную комнату/объект как раньше.
    const req = noPlace
      ? { items: [item], orphan: true }
      : { items: [item], roomId: place!.roomId, propertyId: place!.propertyId };
    items
      .addItems(req)
      .then(() => {
        router.go('tree');
      })
      .catch((err: unknown) => {
        setError(describeError(err));
        setPending(false);
      });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: T.bg }}>
      <ScreenHeader
        title="Новая вещь"
        subtitle={prefill !== undefined ? 'Дополни то, что ассистент не записал' : 'Заполни вручную'}
        onBack={back}
      />
      <div style={{ flex: 1, overflow: 'auto', padding: '4px 16px 16px' }}>
        <FormLabel>Что это?</FormLabel>
        <FormInput
          value={name}
          onChange={setName}
          placeholder="Например: дрель Bosch"
          disabled={pending}
        />

        <FormLabel style={{ marginTop: 18 }}>Количество</FormLabel>
        <FormInput
          value={quantity}
          onChange={setQuantity}
          placeholder="1"
          inputMode="numeric"
          disabled={pending}
        />

        <div style={{ marginTop: 18 }}>
          <IconPicker
            value={icon}
            onChange={setIcon}
            autoIcon={autoIcon}
            disabled={pending}
          />
        </div>

        <FormLabel style={{ marginTop: 18 }}>Место</FormLabel>
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            marginBottom: 10,
            fontSize: 13,
            color: T.ink2,
            cursor: pending ? 'default' : 'pointer',
          }}
        >
          <input
            type="checkbox"
            checked={noPlace}
            disabled={pending}
            onChange={(e) => setNoPlace((e.target as HTMLInputElement).checked)}
          />
          Без места — сложу позже
        </label>
        {noPlace ? (
          <div style={{ fontSize: 12.5, color: T.ink3, padding: '6px 2px' }}>
            Вещь добавится без места. Найти её можно будет в разделе «Без места».
          </div>
        ) : (
          <>
            {tree.status === 'loading' && (
              <div style={{ fontSize: 12.5, color: T.ink3, padding: '6px 2px' }}>Загружаю…</div>
            )}
            {tree.status === 'error' && tree.error !== null && (
              <FormError message={tree.error} />
            )}
            {tree.status === 'ready' && (
              <PlacePicker tree={tree.properties} value={place} onChange={setPlace} />
            )}
          </>
        )}

        {error !== null && <FormError message={error} />}
      </div>
      <FormFooter
        onCancel={back}
        onConfirm={save}
        confirmLabel="Добавить"
        confirmIcon="check"
        disabled={!canSave}
        pending={pending}
      />
    </div>
  );
}
